import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  ITEM_CATALOG,
  itemTitle,
  type ItemCatalogEntry,
  type ItemCategory,
} from "@/lib/item-catalog";
import { cn } from "@/lib/utils";
import { ItemIcon } from "@/components/GameIcon";

interface Props {
  value: string;
  onChange: (itemId: string) => void;
  placeholder?: string;
  className?: string;
}

type CategoryFilter = ItemCategory | "all";

const CATEGORY_TABS: { key: CategoryFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "consumable", label: "Consumable" },
  { key: "material", label: "Material" },
  { key: "rune", label: "Rune" },
  { key: "keyitem", label: "Key" },
  { key: "equipment", label: "Equipment" },
  { key: "costume", label: "Costume" },
];

export function ItemPicker({ value, onChange, placeholder, className }: Props) {
  const selectedLabel = value ? itemTitle(value) : "";

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<CategoryFilter>("all");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Counts shown in the chips reflect the current text query so the user can
  // see at a glance which categories actually contain matches.
  const { filtered, counts } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const counts: Record<CategoryFilter, number> = {
      all: 0,
      consumable: 0,
      material: 0,
      rune: 0,
      keyitem: 0,
      equipment: 0,
      costume: 0,
    };
    const out: ItemCatalogEntry[] = [];
    for (const it of ITEM_CATALOG) {
      if (q && !it.name.toLowerCase().includes(q) && !it.id.toLowerCase().includes(q)) {
        continue;
      }
      counts.all += 1;
      if (it.category) counts[it.category] += 1;
      if (category !== "all" && it.category !== category) continue;
      out.push(it);
    }
    return { filtered: out, counts };
  }, [query, category]);

  function pick(it: ItemCatalogEntry) {
    onChange(it.id);
    setQuery("");
    setOpen(false);
  }

  return (
    <div ref={wrapRef} className={cn("relative", className)}>
      <Input
        value={open ? query : selectedLabel}
        onFocus={() => {
          setOpen(true);
          setQuery("");
        }}
        onChange={(e) => {
          setOpen(true);
          setQuery(e.target.value);
        }}
        placeholder={placeholder ?? "Search item by name…"}
      />
      {value && !open && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground pointer-events-none truncate max-w-[40%]">
          {value}
        </span>
      )}
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-border bg-card shadow-lg">
          <div className="sticky top-0 z-10 flex flex-wrap gap-1 border-b border-border bg-card p-2">
            {CATEGORY_TABS.map((c) => {
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  // Prevent the input from blurring so the user can keep typing
                  // after switching category.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setCategory(c.key)}
                  className={cn(
                    "text-[10px] px-2 py-0.5 rounded-full border transition-colors",
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:bg-accent"
                  )}
                >
                  {c.label}
                  <span className={cn("ml-1 font-mono", active ? "opacity-80" : "opacity-60")}>
                    {counts[c.key]}
                  </span>
                </button>
              );
            })}
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">No matches.</div>
          ) : (
            filtered.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => pick(it)}
                className={cn(
                  "w-full text-left px-3 py-1.5 text-xs hover:bg-accent flex items-center justify-between gap-2",
                  it.id === value && "bg-accent/60"
                )}
              >
                <span className="flex min-w-0 items-center gap-2"><ItemIcon id={it.id} className="h-7 w-7" /><span className="truncate">{it.name}</span></span>
                <span className="font-mono text-[10px] text-muted-foreground shrink-0">{it.id}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
